// src/utils/reveal.js
// ---------------------------------------------------------------------------
// Scroll reveals for the landing page — ONE IntersectionObserver for every
// element that opts in, rather than one per section.
//
// The motion is decoration over content that has to be readable without it, so
// every path that cannot animate lands on the FINAL state: no
// IntersectionObserver, reduced motion requested, or the element already on
// screen at load. Nothing here can leave text invisible.
// ---------------------------------------------------------------------------
import { useEffect, useRef } from 'react';

export function prefersReducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}

let observer = null;

function shared() {
  if (observer) return observer;
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return null;
  observer = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      // Reveal once. Replaying on every scroll back up is motion for its own sake.
      observer.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  return observer;
}

/** Attach the returned ref to an element carrying `lp-reveal` or `lp-stagger`. */
export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = shared();
    if (!obs || prefersReducedMotion()) {
      el.classList.add('is-in');
      return undefined;
    }
    obs.observe(el);
    return () => obs.unobserve(el);
  }, []);
  return ref;
}
